import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { BlueprintGrid } from "../components/BlueprintGrid";
import { fadeUp, viewportOnce } from "../lib/motion";

const CATEGORIES = [
    {
        title: "ABOUT BUILDEASE",
        faqs: [
            {
                q: "What is BuildEase?",
                a: "BuildEase is a South African residential construction intelligence platform. It is being developed to help homeowners better understand architectural plans, quantities, cost estimates, contractor quotations, materials, compliance considerations and design possibilities — in one connected platform.",
            },
            {
                q: "Who is it designed for?",
                a: "Homeowners planning, preparing for or undertaking residential construction — initially homes under 500 m². A separate professional product, BuildEase Pro, is planned for V2.",
            },
            {
                q: "Is BuildEase only available in South Africa?",
                a: "BuildEase is being developed for the South African residential construction market, with Durban and KwaZulu-Natal as the first geographic context. Over time, the vision extends to the broader African market.",
            },
            {
                q: "Does BuildEase replace construction professionals?",
                a: "No. BuildEase supports informed decision-making with better information. It does not replace architects, engineers, quantity surveyors, contractors or other qualified professionals, and it does not provide professional certification or regulatory approval.",
            },
        ],
    },
    {
        title: "PLATFORM",
        faqs: [
            {
                q: "How do I create a project?",
                a: "You create a project by providing basic information about the home you intend to build — location, approximate size and what you are planning. Each independent property is its own project with its own plan.",
            },
            {
                q: "How do I upload architectural plans?",
                a: "Once your project is created, you submit the architectural plans and documents you already have through the platform. Whatever stage your documentation is at, it can come in — outstanding information is flagged rather than invented.",
            },
            {
                q: "What document formats are supported?",
                a: "Common architectural document formats (such as PDF plans and drawings) are supported. Exact format support per capability is being confirmed during the pilot.",
            },
            {
                q: "Can BuildEase generate a BOQ?",
                a: "BuildEase is being developed to organise the construction information available for your project into structured quantities and BOQ-style cost breakdowns, with figures labelled Verified, Estimated or Assumed. It supports your understanding of the document — it is not a substitute for a quantity surveyor's formal BOQ where one is required. See the Sample Report for an illustration.",
            },
            {
                q: "Can BuildEase analyse contractor quotations?",
                a: "Yes — the Quote Analyser breaks quotations into line items and shows neutral assessments: items within an observed range, prices that may require review, and specifications needing clarification. Assessments depend on the information supplied and are not a judgement of any contractor.",
            },
            {
                q: "Can BuildEase visualise my home in 3D?",
                a: "Home Vision explores AI-generated visual concepts of your planned home — exterior, interior and finishes — and is part of the pilot. Broader 3D exploration is a staged capability, clearly labelled in the interface. Visual concepts are not construction drawings.",
            },
            {
                q: "Can I access BuildEase on my phone?",
                a: "BuildEase is web-based and designed to work across devices. Mobile access will continue to improve as the platform develops through the pilot.",
            },
        ],
    },
    {
        title: "PRICING",
        faqs: [
            {
                q: "How does pricing work?",
                a: "One project, one plan, paid once-off — no subscription. Free (R0), Standard (R1,499) and Premium (R3,499) plans are per independent project, with lifetime access to a paid project. See the pricing page for full feature lists and availability labels.",
            },
            {
                q: "Can I manage multiple projects?",
                a: "Yes. One account can hold several projects — each with its own plan.",
            },
            {
                q: "Does each independent home need a separate plan?",
                a: "Yes. A plan belongs to a project, not an account. Revised plans and amendments for the same project never trigger a new charge — a genuinely different property needs its own project.",
            },
            {
                q: "What is included in each plan?",
                a: "Each plan's features, usage limits and availability labels are listed on the pricing page, where every feature is marked At Launch, In Development or Roadmap.",
            },
        ],
    },
    {
        title: "TRUST AND PRIVACY",
        faqs: [
            {
                q: "How accurate are cost estimates?",
                a: "Estimates depend on document quality, the information available and verified pricing data. BuildEase labels each figure Verified, Estimated or Assumed, states the assumptions behind it, and never presents an estimate as a guaranteed cost or a quantity surveyor's formal estimate.",
            },
            {
                q: "How are material prices sourced?",
                a: "Material pricing is sourced in priority order, closest to where you are building: local supplier or branch, then city, province, nearby region — a national reference only where local data isn't available. Sources, dates and units are displayed with the price.",
            },
            {
                q: "How does BuildEase handle architectural documents?",
                a: "Documents you upload belong to your project. They are used to organise and interpret your project's information and are handled with POPIA-conscious care — see the Privacy Policy for details.",
            },
            {
                q: "Does BuildEase provide professional certification?",
                a: "No. BuildEase communicates uncertainty and the importance of appropriate professional review. It does not certify structural safety, approve building compliance or replace qualified professionals.",
            },
        ],
    },
    {
        title: "SUPPORT",
        faqs: [
            {
                q: "How do I contact BuildEase?",
                a: "Email support@buildease.co.za or use the contact form on the website — your enquiry goes straight to the support team.",
            },
            {
                q: "How do I get assistance with my account?",
                a: "Contact support@buildease.co.za with your project details, or send a message through the contact form and the team will follow up.",
            },
        ],
    },
];

const FaqsPage = () => (
    <div className="min-h-screen bg-ink text-white">
        <Seo
            title="Frequently Asked Questions | BuildEase"
            description="Answers about BuildEase: what it is, how projects work, BOQs, quote analysis, Home Vision, pricing per project, data handling and support."
        />
        <Navbar />
        <main>
            <section className="relative overflow-hidden bg-ink pt-[128px] lg:pt-[168px]">
                <BlueprintGrid />
                <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
                    <motion.div variants={fadeUp} initial="hidden" animate="show">
                        <p className="flex items-center gap-3 font-mono text-[13px] tracking-[0.3em] text-mist">
                            <span className="h-1.5 w-1.5 bg-accent" />
                            FAQS
                        </p>
                        <h1
                            data-testid="faq-headline"
                            className="mt-8 max-w-4xl font-display text-4xl font-semibold leading-[1.08] text-white md:text-6xl"
                        >
                            Questions, answered with the same clarity
                            <span className="text-accent">.</span>
                        </h1>
                    </motion.div>
                </div>
            </section>

            <section className="relative py-16 lg:py-24">
                <div className="mx-auto max-w-[900px] px-6 md:px-10 lg:px-16">
                    {CATEGORIES.map((cat, ci) => (
                        <motion.div
                            key={cat.title}
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="show"
                            viewport={viewportOnce}
                            data-testid={`faq-category-${ci + 1}`}
                            className="mt-12 first:mt-0"
                        >
                            <div className="flex items-center gap-4">
                                <p className="font-mono text-[13px] tracking-[0.3em] text-accent">
                                    {cat.title}
                                </p>
                                <span className="h-px flex-1 bg-white/10" />
                            </div>
                            <Accordion type="single" collapsible className="mt-6" data-testid={`faq-accordion-${ci + 1}`}>
                                {cat.faqs.map((f, qi) => (
                                    <AccordionItem
                                        key={f.q}
                                        value={`c${ci}-q${qi}`}
                                        className="border-white/10"
                                    >
                                        <AccordionTrigger className="py-5 text-left text-[16px] font-medium text-white hover:no-underline hover:text-accent">
                                            {f.q}
                                        </AccordionTrigger>
                                        <AccordionContent className="border-l border-accent/40 pl-4 text-[15px] leading-relaxed text-mist">
                                            {f.a}
                                            {f.q === "What is included in each plan?" ? (
                                                <Link
                                                    to="/pricing"
                                                    data-testid="faq-pricing-link"
                                                    className="ml-2 text-accent hover:text-[#ff7026]"
                                                >
                                                    View pricing →
                                                </Link>
                                            ) : null}
                                            {f.q === "Can BuildEase generate a BOQ?" ? (
                                                <Link
                                                    to="/sample-report"
                                                    data-testid="faq-sample-report-link"
                                                    className="ml-2 text-accent hover:text-[#ff7026]"
                                                >
                                                    See the sample report →
                                                </Link>
                                            ) : null}
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </motion.div>
                    ))}

                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={viewportOnce}
                        className="mt-16 border border-white/12 bg-surface/80 px-7 py-8 text-center"
                    >
                        <p className="font-display text-xl font-semibold text-white">
                            Still have a question?
                        </p>
                        <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-mist">
                            Email{" "}
                            <a
                                href="mailto:support@buildease.co.za?subject=BuildEase%20Support%20Request"
                                data-testid="faq-support-email"
                                className="text-accent hover:text-[#ff7026]"
                            >
                                support@buildease.co.za
                            </a>{" "}
                            or use the contact form — the support team will
                            follow up.
                        </p>
                        <Link
                            to="/#contact"
                            data-testid="faq-contact-link"
                            className="mt-6 inline-flex items-center gap-2 bg-accent px-6 py-3.5 text-[15px] font-semibold text-white transition-colors duration-300 hover:bg-[#ff7026]"
                        >
                            Contact BuildEase
                        </Link>
                    </motion.div>
                </div>
            </section>
        </main>
        <Footer />
    </div>
);

export default FaqsPage;
